import { restoreScriptBackup } from "../backup";
import { exportJson, safeExportName } from "../exports";
import { allEntries } from "../parser";
import { store } from "../store";
import { downloadFile, escapeHtml, formatDate, LightElement } from "../ui";
import type { ScriptDocument } from "../types";

export class ScriptLibrary extends LightElement {
  private query = "";
  private scripts: ScriptDocument[] = [];
  private folders: string[] = [];
  private activeFolder: string | null = null;
  private restoreError = "";

  connectedCallback(): void {
    const unsubscribe = store.subscribe((state) => {
      this.scripts = state.scripts;
      this.folders = [...new Set([...state.settings.scriptFolders, ...state.scripts.map((script) => script.folder).filter((folder): folder is string => Boolean(folder))])];
      if (this.activeFolder && !this.folders.includes(this.activeFolder)) this.activeFolder = null;
      this.render(state.ready);
    });
    this.abort.signal.addEventListener("abort", unsubscribe);
  }

  private matchingScripts(): ScriptDocument[] {
    const query = this.query.toLocaleLowerCase();
    return this.scripts.filter((script) => {
      const inFolder = this.activeFolder === null || (this.activeFolder === "" ? !script.folder : script.folder === this.activeFolder);
      if (!inFolder) return false;
      return !query || [script.title, script.folder ?? "", script.notes ?? "", ...(script.tags ?? []), ...script.characters.map((character) => character.name), ...script.sections.map((section) => section.title), ...allEntries(script).map((entry) => entry.text)].some((value) => value.toLocaleLowerCase().includes(query));
    });
  }

  private render(ready: boolean): void {
    const scripts = this.matchingScripts();
    this.innerHTML = `
      <section class="page library-page">
        <div class="hero">
          <p class="eyebrow">Your rehearsal room</p>
          <h1>Know every cue.</h1>
          <p>Import a script, choose your roles, and practise at your pace—even offline.</p>
          <div class="hero__actions"><a class="button" href="#/import">Import a script <span aria-hidden="true">→</span></a>${store.state.settings.enableCollaborativeRehearsal ? '<a class="button button--secondary" href="#/join">Join rehearsal</a>' : ""}<label class="button button--ghost restore-button"><input data-restore type="file" accept=".json,.linebyline.json,application/json">Restore backup</label></div>
          ${this.restoreError ? `<div class="notice notice--error" role="alert">${escapeHtml(this.restoreError)}</div>` : ""}
        </div>
        <div class="section-heading"><div><p class="eyebrow">Library</p><h2>Your scripts</h2></div>
          <label class="search"><span class="sr-only">Search scripts</span><input type="search" placeholder="Search scripts, lines, roles…" value="${escapeHtml(this.query)}"></label>
        </div>
        ${this.folderBar()}
        <div class="script-grid" aria-live="polite">
          ${!ready ? "<p>Opening your library…</p>" : scripts.length ? scripts.map((script) => this.card(script)).join("") : this.empty()}
        </div>
      </section>`;
    this.bind();
  }

  private folderBar(): string {
    const button = (folder: string | null, label: string, count: number) => `<button class="folder-chip ${this.activeFolder === folder ? "is-active" : ""}" data-folder-filter="${escapeHtml(folder ?? "")}" ${folder === null ? "data-all-folders" : ""} aria-pressed="${this.activeFolder === folder}">${escapeHtml(label)} <span>${count}</span></button>`;
    return `<div class="folder-bar">
      <nav class="folder-list" aria-label="Script folders">
        ${button(null, "All", this.scripts.length)}
        ${button("", "Unfiled", this.scripts.filter((script) => !script.folder).length)}
        ${this.folders.map((folder) => button(folder, folder, this.scripts.filter((script) => script.folder === folder).length)).join("")}
      </nav>
      <div class="folder-actions">
        ${this.activeFolder ? `<button class="button button--ghost button--small" data-delete-folder>Delete folder</button>` : ""}
        <button class="button button--secondary button--small" data-new-folder>+ New folder</button>
      </div>
    </div>`;
  }

  private bind(): void {
    const search = this.querySelector<HTMLInputElement>('input[type="search"]');
    if (search) this.listen(search, "input", () => { this.query = search.value; this.render(true); this.querySelector<HTMLInputElement>('input[type="search"]')?.focus(); });
    const restoreInput = this.querySelector<HTMLInputElement>("[data-restore]");
    if (restoreInput) this.listen(restoreInput, "change", () => { const file = restoreInput.files?.[0]; if (file) void this.restore(file); });
    this.querySelectorAll<HTMLButtonElement>("[data-folder-filter]").forEach((button) => this.listen(button, "click", () => {
      this.activeFolder = button.hasAttribute("data-all-folders") ? null : button.dataset.folderFilter ?? null;
      this.render(true);
    }));
    const newFolder = this.querySelector<HTMLElement>("[data-new-folder]");
    if (newFolder) this.listen(newFolder, "click", () => void this.createFolder());
    const deleteFolder = this.querySelector<HTMLElement>("[data-delete-folder]");
    if (deleteFolder) this.listen(deleteFolder, "click", () => void this.deleteFolder());
    this.querySelectorAll<HTMLSelectElement>("[data-folder]").forEach((select) => this.listen(select, "change", () => void this.moveScript(select.dataset.folder ?? "", select.value)));
    this.querySelectorAll<HTMLButtonElement>("[data-backup]").forEach((button) => this.listen(button, "click", () => this.createBackup(button.dataset.backup ?? "")));
    this.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((button) => this.listen(button, "click", () => {
      if (confirm("Delete this script from this device?")) void store.remove(button.dataset.delete ?? "");
    }));
  }

  private card(script: ScriptDocument): string {
    const dialogue = allEntries(script).filter((entry) => entry.kind === "dialogue").length;
    return `<article class="script-card">
      <a class="script-card__body" href="#/player/${script.id}">
        <div class="script-card__icon" aria-hidden="true">${escapeHtml(script.title.charAt(0).toUpperCase())}</div>
        <div><h3>${escapeHtml(script.title)}</h3><p>${script.characters.length} ${script.characters.length === 1 ? "role" : "roles"} · ${dialogue} lines</p>${script.tags?.length ? `<div class="tag-list">${script.tags.slice(0, 3).map((tag) => `<span>${escapeHtml(tag)}</span>`).join("")}</div>` : ""}<small>Updated ${formatDate(script.updatedAt)}</small></div>
      </a>
      <div class="script-card__actions">
        <label><span class="sr-only">Folder for ${escapeHtml(script.title)}</span><select data-folder="${script.id}" aria-label="Folder for ${escapeHtml(script.title)}"><option value="">Unfiled</option>${this.folders.map((folder) => `<option value="${escapeHtml(folder)}" ${script.folder === folder ? "selected" : ""}>${escapeHtml(folder)}</option>`).join("")}</select></label>
        <div class="script-card__button-group">
          <button class="icon-button script-backup-button" data-backup="${script.id}" aria-label="Create backup of ${escapeHtml(script.title)}" title="Back up script">↓</button>
          <button class="icon-button icon-button--danger" data-delete="${script.id}" aria-label="Delete ${escapeHtml(script.title)}" title="Delete script">×</button>
        </div>
      </div>
    </article>`;
  }

  private createBackup(id: string): void {
    const script = this.scripts.find((item) => item.id === id);
    if (!script) return;
    downloadFile(`${safeExportName(script.title)}.linebyline.json`, exportJson(script), "application/json");
  }

  private async createFolder(): Promise<void> {
    const name = prompt("Folder name")?.trim();
    if (!name) return;
    const existing = this.folders.find((folder) => folder.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (existing) { this.activeFolder = existing; this.render(true); return; }
    this.activeFolder = name;
    await store.saveSettings({ ...store.state.settings, scriptFolders: [...this.folders, name] });
  }

  private async deleteFolder(): Promise<void> {
    const folder = this.activeFolder;
    if (!folder || !confirm(`Delete the folder “${folder}”? Its scripts will become unfiled.`)) return;
    const scripts = this.scripts.filter((script) => script.folder === folder);
    for (const script of scripts) {
      script.folder = undefined;
      await store.save(script);
    }
    this.activeFolder = null;
    await store.saveSettings({ ...store.state.settings, scriptFolders: this.folders.filter((item) => item !== folder) });
  }

  private async moveScript(id: string, folder: string): Promise<void> {
    const script = this.scripts.find((item) => item.id === id);
    if (!script) return;
    script.folder = folder || undefined;
    await store.save(script);
  }

  private async restore(file: File): Promise<void> {
    this.restoreError = "";
    try {
      const script = restoreScriptBackup(await file.text(), store.state.settings);
      const existing = this.scripts.some((item) => item.id === script.id);
      if (existing && !confirm(`Replace the existing copy of “${script.title}” with this backup?`)) return;
      if (script.folder && !this.folders.includes(script.folder)) await store.saveSettings({ ...store.state.settings, scriptFolders: [...this.folders, script.folder] });
      await store.save(script);
      location.hash = `#/player/${script.id}`;
    } catch (error) {
      this.restoreError = error instanceof Error ? error.message : "The backup could not be restored.";
      this.render(true);
    }
  }

  private empty(): string {
    const filteredFolder = this.activeFolder !== null;
    return `<div class="empty-state"><div aria-hidden="true" class="empty-state__icon">⌁</div><h3>${this.query ? "No matching scripts" : filteredFolder ? "This folder is empty" : "Your next role starts here"}</h3><p>${this.query ? "Try another search." : filteredFolder ? "Use the folder menu on a script card to move it here." : "Import a TXT, DOCX, PDF, or paste text to begin."}</p>${this.query || filteredFolder ? "" : '<a class="button button--secondary" href="#/import">Choose a script</a>'}</div>`;
  }
}

customElements.define("script-library", ScriptLibrary);
